import { ChatModel } from '../chat.model';
import type { Conversation } from '@cotera/sdk/core';
import { ToolPart } from '@cotera/sdk/core';
import {
  ReadonlyWatchable,
  useWatchableValue,
  Watchable,
} from '../../actions/framework';

export type ToolRendererProps = {
  part: ToolPart;
  chatModel: Pick<ChatModel, 'addToolResult' | 'id' | 'model'> & {
    /** When present, used to derive owner org for cross-org credential UI. */
    conversationWatchable?: ReadonlyWatchable<Conversation | null>;
  };
  readonly?: boolean;
  /**
   * True when the renderer is drawn inside a grouped "Run N" row (a run of
   * repeated same-tool calls). The row already supplies its own collapsible
   * header/label, so a renderer should skip its own `<Tool>`/`<ToolHeader>`
   * chrome here and render only its body — otherwise the tool name repeats
   * under the group heading.
   */
  nested?: boolean;
};

const UNDEFINED_CONVERSATION_WATCHABLE = Watchable.fromValue<
  Conversation | undefined
>(undefined);

/** Returns the chat's owning org. Use for cross-org credential UI. Undefined when not available (e.g. sub-agent). */
export function useOwnerOrgId(
  chatModel: ToolRendererProps['chatModel']
): string | undefined {
  const conversationWatchable = chatModel.conversationWatchable;
  const conversation = useWatchableValue(
    conversationWatchable ?? UNDEFINED_CONVERSATION_WATCHABLE
  );
  if (conversationWatchable === undefined) {
    return undefined;
  }
  return conversation?.getOwnerOrgId() ?? undefined;
}
